import {defineSemanticTokens, defineTokens} from "@pandacss/dev"

import {defineTheme} from "./defineTheme"
import {
  sharedBaseColors,
  sharedCopperTaupePalette,
  sharedErrorPalette,
  sharedGrayPalette,
  sharedNeutralPalette,
  sharedSteelFogPalette,
  sharedUtilityColors,
  sharedYellowPalette,
} from "./tokens/shared"
import {keyframes, spacing, textStyles, zIndex} from "./utils"

export const defaultTheme = defineTheme({
  breakpoints: {
    desktop: "768px",
    largeDesktop: "1280px",
  },
  tokens: defineTokens({
    colors: defineTokens.colors({
      ...sharedUtilityColors,
      darkBlue: {value: "#082065"},
      royalBlue: {value: "#1740BB"},
      lightSkyBlue: {value: "#C0D2EB"},
      neutral: sharedNeutralPalette,
      // Orange
      brandAccent: {
        50: {value: "#feece4"},
        100: {value: "#fce0d3"},
        200: {value: "#eec4b1"},
        300: {value: "#e7a688"},
        400: {value: "#ea8d66"},
        500: {value: "#f26e3a"},
        600: {value: "#ff4902"},
        700: {value: "#d64105"},
        800: {value: "#852a05"},
        900: {value: "#431a0a"},
        950: {value: "#2b1208"},
      },
      base: sharedBaseColors,
      // Metal Gray
      brand: {
        50: {value: "#e9f5f9"},
        100: {value: "#d6e6eb"},
        200: {value: "#baced4"},
        300: {value: "#9eb2b8"},
        400: {value: "#79939a"},
        500: {value: "#537179"},
        600: {value: "#3a5b64"},
        700: {value: "#264750"},
        800: {value: "#17333a"},
        900: {value: "#0f2429"},
        950: {value: "#09171a"},
      },
      steelFog: sharedSteelFogPalette,
      copperTaupe: sharedCopperTaupePalette,
      error: sharedErrorPalette,
      yellow: sharedYellowPalette,
      gray: sharedGrayPalette,
    }),
    zIndex,
    spacing,
    gradients: defineTokens.gradients({
      orangeGradient: {
        value:
          "linear-gradient(231.14deg, rgba(255, 73, 2, 0.4) 0%, rgba(250, 250, 250, 0.4) 92.75%)",
      },
      orangeBorderGradient: {
        value: "linear-gradient(232.29deg, #FF4902 1.1%, #FFFFFF 87.26%)",
      },
      whiteLinearGradient: {
        value:
          "linear-gradient(180deg, rgba(214, 230, 235, 0) 0%, #D6E6EB 100%)",
      },
    }),
  }),
  semanticTokens: defineSemanticTokens({
    colors: defineSemanticTokens.colors({
      background: {
        primary: {value: "{colors.base.white}"},
        primaryOnSolid: {value: "{colors.lightBlack}"},
        secondary: {value: "{colors.neutral.100}"},
        tertiary: {value: "{colors.neutral.200}"},
        quaternary: {value: "{colors.neutral.300}"},
        muted: {value: "{colors.neutral.50}"},
        subtle: {
          primary: {value: "{colors.neutral.50}"},
          brand: {value: "{colors.brandAccent.50}"},
        },
        brand: {
          primary: {value: "{colors.brand.100}"},
          primarySubtle: {value: "{colors.brand.50}"},
          secondary: {value: "{colors.brand.200}"},
          solid: {value: "{colors.brand.700}"},
          solidSubtle: {value: "{colors.brand.600}"},
          accent: {value: "{colors.brandAccent.600}"},
          accentSolid: {value: "{colors.brandAccent.600}"},
          accentSolidHover: {value: "{colors.brandAccent.700}"},
          utility: {value: "{colors.brand.800}"},
        },
        causesPanel: {value: "{colors.neutral.100}"},
        causesCard: {value: "transparent"},
        causesCardHover: {value: "transparent"},
        error: {value: "{colors.error.100}"},
        warn: {value: "{colors.yellow.50}"},
        success: {value: "{colors.bgGreen}"},
        disabled: {value: "{colors.neutral.100}"},
        tagTransparent: {value: "#FFFFFF14"},
      },
      foreground: {
        primary: {value: "{colors.neutral.900}"},
        primarySubtle: {value: "{colors.neutral.700}"},
        secondary: {value: "{colors.neutral.800}"},
        tertiary: {value: "{colors.neutral.600}"},
        quaternary: {value: "{colors.neutral.500}"},
        primaryOnSolid: {value: "{colors.base.white}"},
        brand: {
          primary: {value: "{colors.brand.700}"},
          secondary: {value: "{colors.brand.600}"},
          accent: {value: "{colors.brandAccent.700}"},
        },
        error: {
          primary: {value: "{colors.error.600}"},
        },
        warning: {
          primary: {value: "{colors.yellow.600}"},
        },
        secondaryOnSolid: {value: "{colors.brand.300}"},
        disabled: {value: "{colors.neutral.400}"},
        success: {value: "{colors.green}"},
      },
      text: {
        brand: {
          primary: {value: "{colors.brand.700}"},
          accent: {value: "{colors.brandAccent.700}"},
          secondary: {value: "{colors.brand.600}"},
        },
        warning: {
          primary: {value: "{colors.yellow.200}"},
        },
        quaternary: {value: "{colors.neutral.500}"},
        tertiary: {value: "{colors.neutral.600}"},
        secondary: {
          DEFAULT: {value: "{colors.neutral.800}"},
          onSolid: {value: "{colors.brand.300}"},
        },
        error: {
          primary: {value: "{colors.error.600}"},
        },
        primary: {
          DEFAULT: {value: "{colors.base.black}"},
          subtle: {value: "{colors.neutral.600}"},
          onSolid: {value: "{colors.base.white}"},
        },
        link: {
          DEFAULT: {value: "{colors.brandAccent.600}"},
          hover: {value: "{colors.brandAccent.700}"},
        },
        causesPanel: {
          primary: {value: "{colors.base.black}"},
          muted: {value: "{colors.neutral.600}"},
          pattern: {value: "{colors.neutral.300}"},
        },
        disabled: {value: "{colors.neutral.400}"},
        success: {value: "{colors.green}"},
      },
      border: {
        brand: {
          primary: {value: "{colors.brand.300}"},
          primarySolid: {value: "{colors.brand.700}"},
          secondarySolid: {value: "{colors.brand.600}"},
          secondary: {value: "{colors.brand.200}"},
          accent: {value: "{colors.brandAccent.600}"},
          accentHover: {value: "{colors.brandAccent.700}"},
        },
        secondary: {
          solid: {value: "{colors.neutral.600}"},
          200: {value: "{colors.neutral.200}"},
        },
        primary: {
          300: {value: "{colors.neutral.300}"},
          solid: {
            700: {value: "{colors.neutral.700}"},
          },
        },
        onWarning: {value: "{colors.yellow.600}"},
        error: {value: "{colors.error.600}"},
        disabled: {value: "{colors.neutral.300}"},
        successSubtle: {value: "{colors.green}"},
        errorSubtle: {value: "{colors.error.300}"},
        causeCard: {value: "{colors.steelFog.400}"},
      },
      confirmedIconsBackground: {
        100: {value: "{colors.brand.500}"},
        200: {value: "{colors.brand.700}"},
        300: {value: "{colors.brand.800}"},
        400: {value: "{colors.steelFog.500}"},
        500: {value: "{colors.steelFog.600}"},
        600: {value: "{colors.steelFog.700}"},
        700: {value: "{colors.copperTaupe.500}"},
        800: {value: "{colors.copperTaupe.600}"},
        900: {value: "{colors.copperTaupe.700}"},
      },
    }),
    gradients: {
      causesHoverGradient: {
        value: "{gradients.orangeGradient}",
      },
      causesHoverBorderGradient: {
        value: "{gradients.orangeBorderGradient}",
      },
      conversationFade: {
        value: "{gradients.whiteLinearGradient}",
      },
      // Opaque everywhere = no fade; this brand's pattern isn't dense enough
      // to need dampening behind the cause list.
      causesPatternMask: {
        value: "linear-gradient(to bottom, black, black)",
      },
      aiAccentPrimary: {
        value: "linear-gradient(135deg, #00A3E0 0%, #0041C2 100%)",
      },
      aiAccentDanger: {
        value: "linear-gradient(134deg, #FF8784 -1.91%, #FF2C56 100%)",
      },
    },
    radii: {
      causeCard: {value: "0px"},
    },
    borderWidths: {
      causeCardSide: {value: "0px"},
    },
    spacing: {
      causesCardGap: {value: "0px"},
    },
  }),
  textStyles,
  keyframes,
})
